import React, { useState } from "react";
import NavigationLayout from "pages/navigation-layout";
import ApplicationProvider from "pages/application-provider";
import { Box, Flex, rem, Space, Title, Text, Input, ActionIcon, Divider, Menu, Pagination, Button } from "@mantine/core";
import { IconFilter, IconSearch, IconSortAscending, IconSortDescending, IconX, IconChevronDown } from "@tabler/icons-react";
import PersonCard from "components/matching/PersonCard";
import PersonCardHeader from "components/matching/PersonCardHeader";
import AsideContent from "components/matching/AsideContent";
import { Person } from "lib/types";

interface MatchingPageProps {
    isAuthenticated: string;
    csrfToken: string;
    logoutUrl: string;
    persons: string;
    personsTotal: string;
    userFirstname: string;
    userLastname:string;
    searchTerm: string;
    sort: string;
    rha: string;
    chn: string;
    userDefaultRha: string;
    userDefaultChn: string;
    rhaTeams: string;
    chns: string;
    gender: string;
    pageSize?: string;
    currentPage?: string;
    totalPages?: string;
    staticBaseUrl: string;
}

interface MenuOption {
    value: string;
    label: string;
}

export default function MatchingPage(props: MatchingPageProps) {
    const persons = JSON.parse(props.persons);
    const pageSize = parseInt(props.pageSize || '10', 10);
    const currentPage = parseInt(props.currentPage || '1', 10);
    const totalPages = parseInt(props.totalPages || '1', 10);

    const [open, setOpen] = useState(false);
    const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>(props.searchTerm || '');
    const [rhaFilter] = useState<string>(props.rha || props.userDefaultRha || '');
    const [chnFilter] = useState<string>(props.chn || props.userDefaultChn || '');
    const [genderFilter] = useState<string>(props.gender || '');

    const genderOptions: MenuOption[] = [
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' },
        { value: 'other', label: 'Other' },
    ];

    const initialSortDirection = props.sort?.startsWith('-') ? 'desc' : 'asc';
    const initialSortField = props.sort?.replace(/^-/, '') || 'match_score';
    const [sortField] = useState<string>(initialSortField);
    const [sortDirection] = useState<'asc' | 'desc'>(initialSortDirection);
    
    const rhaOptions: MenuOption[] = JSON.parse(props.rhaTeams).map((rha: string) => ({ value: rha, label: rha }));
    const chnOptions: MenuOption[] = JSON.parse(props.chns).map((chn: string) => ({ value: chn, label: chn }));

    const sortableFields: MenuOption[] = [
        { value: 'dob', label: 'Date of Birth' },
        { value: 'waiting_for_match_since', label: 'Waiting Since' },
        { value: 'match_score', label: 'Match Score' },
    ];
    
    const buildQueryString = (exclude: string[] = []) => {
        const params = new URLSearchParams();
        if (sortField && !exclude.includes('sort')) {
            params.set('sort', `${sortDirection === 'desc' ? '-' : ''}${sortField}`);
        }
        if (searchTerm && !exclude.includes('search')) {
            params.set('search', searchTerm);
        }
        if (rhaFilter && !exclude.includes('rha')) {
            params.set('rha', rhaFilter);
        }
        if (chnFilter && !exclude.includes('chn')) {
            params.set('chn', chnFilter);
        }
        if (genderFilter && !exclude.includes('gender')) {
            params.set('gender', genderFilter);
        }
        return params.toString();
    };
    
    const navigateToPage = (page: number) => {
        if (page < 1 || page > totalPages) return;
        const query = buildQueryString();
        window.location.href = `?page=${page}&page_size=${pageSize}&${query}`;
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        const params = new URLSearchParams(buildQueryString(['search']));
        if (searchTerm) {
            params.set('search', searchTerm);
        }
        window.location.href = `?${params.toString()}`;
    };

    const handleSort = (field: string) => {
        const newDirection = (field === sortField && sortDirection === 'desc') ? 'asc' : 'desc';
        const params = new URLSearchParams(buildQueryString(['sort']));
        params.set('sort', `${newDirection === 'desc' ? '-' : ''}${field}`);
        window.location.href = `?${params.toString()}`;
    };
    
    const handleRhaFilter = (rha: string | null) => {
        const params = new URLSearchParams(buildQueryString(['rha']));
        if (rha) {
            params.set('rha', rha);
        }
        window.location.href = `?${params.toString()}`;
    };
    
    const handleChnFilter = (chn: string | null) => {
        const params = new URLSearchParams(buildQueryString(['chn']));
        if (chn) {
            params.set('chn', chn);
        }
        window.location.href = `?${params.toString()}`;
    };

    const handleGenderFilter = (gender: string | null) => {
        const params = new URLSearchParams(buildQueryString(['gender']));
        if (gender) {
            params.set('gender', gender);
        }
        window.location.href = `?${params.toString()}`;
    };

    return (
        <ApplicationProvider>
            <NavigationLayout
                {...props}
                mainContainerColor={"#F4F4F4"}
                asideOpened={open}
                asideComponent={
                    selectedPerson &&
                    <AsideContent
                        person={selectedPerson}
                        staticBaseUrl={props.staticBaseUrl}
                        closeAside={() => setOpen(false)}
                    />
                }
            >
                <Box w='100%' p={rem(40)}>
                    <Title fz={rem(40)} lh={rem(48)} c={'#272B30'} fw={600}>Older People</Title>
                    <Space h={rem(24)} />
                    <Box bg={'#FCFCFC'} w={'100%'} p={rem(12)} style={{ borderRadius: rem(12) }}>
                        {/* Header */}
                        <Flex direction={'row'} w={'100%'} p={rem(12)}>
                            <Flex gap={rem(16)} direction={'row'} align={'center'} justify={'space-evenly'} w={'100%'}>
                                <Box w={rem(16)} h={rem(44)} bg={'#4B49AC'} style={{ borderRadius: rem(4) }}></Box>
                                <Flex direction='column'>
                                    <Text c={'#1A1D1F'} fz={rem(20)} lh={rem(22)} fw={600}>Older People</Text>
                                    <Text c={'#6F767E'} fz={rem(15)} lh={rem(22)} fw={600}>Total {props.personsTotal}</Text>
                                </Flex>

                                <Flex flex={1} align={"center"} justify={"center"}>
                                    <Box mx={rem(24)} maw={rem(960)} w='100%'>
                                        <form onSubmit={handleSearch} style={{ width: '100%' }}>
                                            <Input
                                                w={'100%'}
                                                leftSection={<IconSearch size={16} />}
                                                placeholder="Search by name"
                                                radius={'md'}
                                                variant={'filled'}
                                                name="search"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </form>
                                    </Box>
                                </Flex>

                                <Flex direction={'row'} gap={rem(8)}>
                                    <Menu shadow="md" width={200}>
                                        <Menu.Target>
                                            {genderFilter ? (
                                                <Button
                                                    variant="light"
                                                    color="gray"
                                                    h={rem(36)}
                                                    rightSection={<IconX size={14} onMouseDown={(e) => e.stopPropagation()} onClick={() => handleGenderFilter(null)} />}
                                                >
                                                    Gender: {genderFilter.charAt(0).toUpperCase() + genderFilter.slice(1)}
                                                </Button>
                                            ) : (
                                                <Button variant="default" h={rem(36)} rightSection={<IconChevronDown size={14} />}>
                                                    Gender
                                                </Button>
                                            )}
                                        </Menu.Target>
                                        <Menu.Dropdown>
                                            <Menu.Label>Filter by Gender</Menu.Label>
                                            <Menu.Item color="red" leftSection={<IconX size={14} />} onClick={() => handleGenderFilter(null)}>Clear Filter</Menu.Item>
                                            <Menu.Item onClick={() => handleGenderFilter('all')}>All</Menu.Item>
                                            <Menu.Divider />
                                            {genderOptions.map((option) => (
                                                <Menu.Item key={option.value} onClick={() => handleGenderFilter(option.value)}>
                                                    {option.label}
                                                </Menu.Item>
                                            ))}
                                        </Menu.Dropdown>
                                    </Menu>
                                    <Menu shadow="md" width={200}>
                                        <Menu.Target>
                                            {chnFilter ? (
                                                <Button
                                                    variant="light"
                                                    color="gray"
                                                    h={rem(36)}
                                                    rightSection={<IconX size={14} onMouseDown={(e) => e.stopPropagation()} onClick={() => handleChnFilter(null)} />}
                                                >
                                                    CHN: {chnFilter}
                                                </Button>
                                            ) : (
                                                <Button variant="default" h={rem(36)} rightSection={<IconChevronDown size={14} />}>
                                                    CHN
                                                </Button>
                                            )}
                                        </Menu.Target>
                                        <Menu.Dropdown mah={280} style={{ overflow: 'auto' }}>
                                            <Menu.Label>Filter by CHN</Menu.Label>
                                            <Menu.Item
                                                color="red"
                                                leftSection={<IconX size={14} />}
                                                onClick={() => handleChnFilter(null)}
                                            >
                                                Clear Filter
                                            </Menu.Item>
                                            <Menu.Divider />
                                            {chnOptions.map((option) => (
                                                <Menu.Item
                                                    key={option.value}
                                                    onClick={() => handleChnFilter(option.value)}
                                                >
                                                    {option.label}
                                                </Menu.Item>
                                            ))}
                                        </Menu.Dropdown>
                                    </Menu>
                                    <Menu shadow="md" width={200}>
                                        <Menu.Target>
                                            {rhaFilter ? (
                                                <Button
                                                    variant="light"
                                                    color="gray"
                                                    h={rem(36)}
                                                    rightSection={<IconX size={14} onMouseDown={(e) => e.stopPropagation()} onClick={() => handleRhaFilter(null)} />}
                                                >
                                                    RHA: {rhaFilter}
                                                </Button>
                                            ) : (
                                                <Button variant="default" h={rem(36)} rightSection={<IconChevronDown size={14} />}>
                                                    RHA
                                                </Button>
                                            )}
                                        </Menu.Target>
                                        <Menu.Dropdown mah={280} style={{ overflow: 'auto' }}>
                                            <Menu.Label>Filter by RHA</Menu.Label>
                                            <Menu.Item
                                                color="red"
                                                leftSection={<IconX size={14} />}
                                                onClick={() => handleRhaFilter(null)}
                                            >
                                                Clear Filter
                                            </Menu.Item>
                                            <Menu.Divider />
                                            {rhaOptions.map((option) => (
                                                <Menu.Item
                                                    key={option.value}
                                                    onClick={() => handleRhaFilter(option.value)}
                                                >
                                                    {option.label}
                                                </Menu.Item>
                                            ))}
                                        </Menu.Dropdown>
                                    </Menu>
                                    <Menu shadow="md" width={200}>
                                        <Menu.Target>
                                            <ActionIcon variant="transparent" style={{ border: '2px solid #EFEFEF' }} aria-label="Sort" h={rem(36)} w={rem(36)}>
                                                {sortDirection === 'asc'
                                                    ? <IconSortAscending color={'#6F767E'} style={{ width: '70%', height: '70%' }} stroke={2} />
                                                    : <IconSortDescending color={'#6F767E'} style={{ width: '70%', height: '70%' }} stroke={2} />}
                                            </ActionIcon>
                                        </Menu.Target>
                                        <Menu.Dropdown>
                                            <Menu.Label>Sort by</Menu.Label>
                                            {sortableFields.map((field) => (
                                                <Menu.Item
                                                    key={field.value}
                                                    leftSection={sortField === field.value ? (
                                                        sortDirection === 'asc' ? <IconSortAscending size={14} /> : <IconSortDescending size={14} />
                                                    ) : null}
                                                    onClick={() => handleSort(field.value)}
                                                >
                                                    {field.label}
                                                </Menu.Item>
                                            ))}
                                        </Menu.Dropdown>
                                    </Menu>
                                </Flex>
                            </Flex>
                        </Flex>

                        <Space h={rem(20)} />

                        <PersonCardHeader />
                        <Divider my={rem(12)} color={'#EFEFEF'} />

                        {persons.map((person: any) => (
                            <Box key={person.pk}>
                                <PersonCard
                                    id={person.pk}
                                    name={person.fields.name}
                                    fullAddress={person.fields.full_address}
                                    daysWaiting={person.fields.days_waiting}
                                    age={person.fields.age}
                                    gender={person.fields.gender}
                                    genderPreference={person.fields.gender_preference}
                                    dementiaPresent={person.fields.dementia_present}
                                    additionalLanguages={person.fields.additionalLanguages}
                                    volunteerMatches={person.fields.matches_count}
                                    smoker={person.fields.smoker}
                                    availability={person.fields.availability}
                                    salesforceId={person.fields.salesforce_id}
                                    chn={person.fields.chn}
                                    rha={person.fields.rha}
                                    onClick={() => { setOpen(true); setSelectedPerson(person); }}
                                />
                                {persons.indexOf(person) < persons.length - 1 && <Divider my={rem(12)} color={'#EFEFEF'} />}
                            </Box>
                        ))}

                        <Flex w={'100%'} mt={rem(20)} align={'center'} justify={'center'}>
                            <Pagination value={currentPage} onChange={navigateToPage} total={totalPages} color="gray" />
                        </Flex>
                    </Box>
                </Box>
            </NavigationLayout>
        </ApplicationProvider>
    );
}